import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { updateEmployee } from '../../employees/actions/update-employee';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import { addEmployeeGroup } from '../actions/add-employee-group';
import { loadEmployeeGroups } from '../actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../employee-group-selectors';

import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';

import { EmployeeGroupsBase } from './employee-groups-base';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  employees: employeesForOrgIdSelector(state, ownProps.employee.orgId),
  employeeGroups: employeeGroupsForOrgIdSelector(state, ownProps.employee.orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadOrgEmployees: () =>
      dispatch(loadOrgEmployeesByStatus(ownProps.employee.orgId, EMPLOYEE_STATUS.CURRENT)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(ownProps.employee.orgId)),
    updateEmployee: (update: Partial<EmployeeUpdateRequest>) =>
      dispatch(updateEmployee(ownProps.employee.employeeId, update)),
    addOrgEmployeeGroup: (name: string) =>
      dispatch(addEmployeeGroup(ownProps.employee.orgId, name)),
  },
});

type InputProps = {
  employee: Employee;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeGroups = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ employee, actions }: Props) => {
  const employeeGroupIds = employee.EmployeeGroups.map((g) => g.employeeGroupId);

  const handleRemove = (removeId: number) => {
    const newGroupIds = employee.EmployeeGroups.map((g) => g.employeeGroupId).filter(
      (id) => id !== removeId,
    );
    actions.updateEmployee({ EmployeeGroups: newGroupIds });

    ProductTelemetryController.logEvent('employee removed from group', {
      value: employee.employeeId,
      metadata: {
        employeeId: String(employee.employeeId),
        employeeGroupId: String(removeId),
      },
    });
  };

  const handleAdd = (addId: number) => {
    const newGroupIds = employee.EmployeeGroups.map((g) => g.employeeGroupId).concat([addId]);
    actions.updateEmployee({ EmployeeGroups: newGroupIds });

    ProductTelemetryController.logEvent('employee added to group', {
      value: employee.employeeId,
      metadata: {
        employeeId: String(employee.employeeId),
        employeeGroupId: String(addId),
      },
    });
  };

  const handleCreate = async (name: string) => {
    const { employeeGroup } = await actions.addOrgEmployeeGroup(name);
    handleAdd(employeeGroup.employeeGroupId);
  };

  return (
    <EmployeeGroupsBase
      orgId={employee.orgId}
      employeeGroupIds={employeeGroupIds}
      onRemove={handleRemove}
      onAdd={handleAdd}
      onCreate={handleCreate}
    />
  );
});
